"use client";

import { getQueryClient } from "@pharaday/utils";
import type { Session } from "next-auth";
import { SessionProvider, useSession } from "next-auth/react";
import { usePathname, useSearchParams } from "next/navigation";
import { type ReactNode, Suspense, useEffect, useMemo, useRef, useState } from "react";

import { type AccessTokenPayload, AuthErrorEnum } from "../../core";
import { AuthenticatorProvider, AuthProvider } from "../../core/application";
import { AuthContainer } from "../../core/auth.container";
import { AuthenticationContext, type AuthenticationContextValue } from "./context";
import usePreviousState from "./usePreviousState";

interface AuthenticationProviderProps {
  children: ReactNode;
  /**
   * next-auth session
   */
  session?: Session | null;
  signOutRedirectUrl: string;
  onAuthenticated?: (payload: AccessTokenPayload) => void;
  onUnauthenticated?: () => void;
}

type SessionHandlerProps = Omit<AuthenticationProviderProps, "session">;

type SessionStatus = ReturnType<typeof useSession>["status"];

interface SignOutRedirectProps {
  status: SessionStatus;
  previousStatus: SessionStatus | undefined;
  sessionError: AuthErrorEnum | undefined;
  signOutRedirectUrl: string;
}

/**
 * Reads the URL only to decide where to send a user who has just been signed out. It renders
 * nothing, so it sits behind its own `<Suspense>` boundary and leaves the rest of the app in the
 * static shell. Keep the URL hooks here rather than in a provider that wraps `children`.
 */
const SignOutRedirect = ({ status, previousStatus, sessionError, signOutRedirectUrl }: SignOutRedirectProps) => {
  const queryClient = getQueryClient();
  const searchParams = useSearchParams();
  const pathName = usePathname();

  useEffect(() => {
    if (status === "unauthenticated" && previousStatus === "authenticated") {
      if (sessionError && searchParams.get("error") !== sessionError) {
        const params = new URLSearchParams();
        params.set("error", sessionError);
        globalThis.location.href = `${signOutRedirectUrl}?${params.toString()}`;
      } else if (pathName !== signOutRedirectUrl) {
        globalThis.location.href = signOutRedirectUrl;
      }
      queryClient.clear();
    }
  }, [status, signOutRedirectUrl, previousStatus, sessionError, searchParams, pathName, queryClient]);

  return null;
};

const SessionHandler = ({ signOutRedirectUrl, onAuthenticated, onUnauthenticated, children }: SessionHandlerProps) => {
  const { data: session, status } = useSession();
  const [sessionError, setSessionError] = useState<AuthErrorEnum>();

  const signOutRef = useRef<Promise<unknown>>(undefined);
  const previousStatus = usePreviousState(status);

  useEffect(() => {
    if (!session) {
      return;
    }

    if (session.error === AuthErrorEnum.REFRESH_ACCESS_TOKEN_ERROR) {
      setSessionError(AuthErrorEnum.REFRESH_ACCESS_TOKEN_ERROR);
      const handleRefreshAccessTokenError = () => {
        if (!signOutRef.current) {
          signOutRef.current = AuthContainer.get(AuthProvider)
            .signOut()
            .finally(() => (signOutRef.current = undefined));
        }
      };

      handleRefreshAccessTokenError();
      return;
    }

    void AuthContainer.get(AuthProvider).updateSession(session);
  }, [session]);

  useEffect(() => {
    if (previousStatus !== "authenticated" && status === "authenticated") {
      setSessionError(undefined);
    }
  }, [status, previousStatus]);

  useEffect(() => {
    if (status === "unauthenticated") {
      onUnauthenticated?.();
    }

    if (status === "authenticated") {
      const accessToken = AuthContainer.get(AuthenticatorProvider).extractAccessToken(session.accessToken);
      onAuthenticated?.(accessToken);
    }
  }, [status, session]);

  const authenticationContext = useMemo<AuthenticationContextValue>(
    () => ({
      authenticated: status === "authenticated",
      accessTokenPayload: session
        ? AuthContainer.get(AuthenticatorProvider).extractAccessToken(session.accessToken)
        : undefined,
    }),
    [session, status]
  );

  return (
    <AuthenticationContext value={authenticationContext}>
      <Suspense>
        <SignOutRedirect
          status={status}
          previousStatus={previousStatus}
          sessionError={sessionError}
          signOutRedirectUrl={signOutRedirectUrl}
        />
      </Suspense>
      {children}
    </AuthenticationContext>
  );
};

const AuthenticationProvider = ({
  children,
  session,
  signOutRedirectUrl,
  onAuthenticated,
  onUnauthenticated,
}: AuthenticationProviderProps) => {
  return (
    <SessionProvider session={session} refetchInterval={0}>
      <SessionHandler
        signOutRedirectUrl={signOutRedirectUrl}
        onAuthenticated={onAuthenticated}
        onUnauthenticated={onUnauthenticated}
      >
        {children}
      </SessionHandler>
    </SessionProvider>
  );
};

export default AuthenticationProvider;
